import { useCallback, useMemo } from "react";
import { Alert, Checkbox, Collapse, ColorPicker, Input, Radio, Space, Typography } from "antd";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import type { ControllerKind, DynamicControllerComponentSpec, MetricOption, SubjectEntity } from "../../Processing/types";
import {
  useControllerDependents,
  useControllers,
  useDashboard,
  useSlicerVariables,
} from "../../Contexts/dashboardHooks";
import {
  CONTROLLER_KINDS,
  SELECTABLE_CONTROLLER_KINDS,
  SLICE_COLOR_PALETTE,
  controllerBareId,
  sliceColor,
  templateText,
  widgetLabels,
} from "../../Processing/controllerBindings";
import { parseTokens, variablesForControls } from "../../Utils/resolveTemplate";
import { deriveKey } from "../../Utils/slicerKeys.mjs";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { PreviewShell } from "../shared/PreviewShell";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { confirmControlsNarrowingBlocked } from "../../Components/dashboardConfirms";
import {
  dynamicControllerReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
  keyProblem,
} from "./dynamicControllerState";

const { Panel } = Collapse;
const { Text } = Typography;

const EMPTY_METRICS: MetricOption[] = [];

/** Compact names for the header summary — the full labels are far too long stacked inline. */
const CONTROL_SHORT_LABELS: Record<ControllerKind, string> = {
  date: "Dates",
  single_date: "Day",
  single_player: "Athlete",
  subject: "Subjects",
};

const CONTROL_LABELS: Record<ControllerKind, string> = {
  date: "Date range picker",
  single_date: "Single date picker (legacy)",
  single_player: "Single player selector (legacy)",
  subject: "Athletes & drills picker",
};

const unique = (titles: string[]): string[] => [...new Set(titles)];

export function DynamicControllerConfigurator({
  onSpecChange,
  existingSpec,
  getSubjectEntities,
  widgetId,
}: ConfiguratorSharedProps<DynamicControllerComponentSpec>) {
  const { state: dashboardState } = useDashboard();
  const dependents = useControllerDependents();
  const controllers = useControllers();
  const slicerValues = useSlicerVariables();

  const controllerUuid = widgetId ? controllerBareId(widgetId) : null;

  // The key the board reads for this slicer, which its tokens quote. A slicer on the board
  // never changes it. Null while adding a new slicer, whose key follows its title.
  const boardKey = useMemo(
    () => controllers.find((c) => c.uuid === controllerUuid)?.key ?? null,
    [controllers, controllerUuid],
  );

  // The keys the other slicers hold: a derived key stays clear of them.
  const otherKeys = useMemo(
    () => controllers.filter((c) => c.uuid !== controllerUuid).map((c) => c.key),
    [controllers, controllerUuid],
  );

  const toSpec = useCallback(
    (
      formState: Parameters<typeof formStateToSpec>[0],
      metrics: MetricOption[],
      subjectEntities?: (t: string) => SubjectEntity[],
    ) => formStateToSpec(formState, metrics, subjectEntities, otherKeys),
    [otherKeys],
  );

  const [state, dispatch] = useConfiguratorSync({
    reducer: dynamicControllerReducer,
    existingSpec,
    hydrate: (spec: DynamicControllerComponentSpec) => hydrateFromSpec(spec, otherKeys, boardKey),
    createInitial: () =>
      createInitialState(
        controllers.map((c) => c.label),
        controllers.map((c) => sliceColor(c.spec.slicer_builder_misc)),
        controllers.map((c) => c.key),
      ),
    toSpec,
    onSpecChange,
    metrics: EMPTY_METRICS,
    getSubjectEntities,
  });

  const problem = keyProblem(state.key, otherKeys);
  const exampleVariable = variablesForControls(state.controls)[0] ?? "StartDate";

  /**
   * Removing a kind would strand every widget that binds that kind here, exactly as a
   * deletion would. Refuse it and name the widgets so the user can unbind them first.
   */
  const dependentEntry = controllerUuid ? dependents[controllerUuid] : undefined;
  const boundWidgetTitles = useMemo<Record<ControllerKind, string[]>>(() => ({
    date: widgetLabels(dependentEntry?.date ?? [], dashboardState.widgets, slicerValues),
    single_date: widgetLabels(dependentEntry?.single_date ?? [], dashboardState.widgets, slicerValues),
    single_player: widgetLabels(dependentEntry?.single_player ?? [], dashboardState.widgets, slicerValues),
    subject: widgetLabels(dependentEntry?.subject ?? [], dashboardState.widgets, slicerValues),
  }), [dependentEntry, dashboardState.widgets, slicerValues]);

  /**
   * The title and text widgets whose tokens quote a variable this slicer would no longer
   * offer with `nextControls`: `{Slicer.WEEK.StartDate}` strands when the date picker goes.
   */
  const tokenStranded = (nextControls: ControllerKind[]): string[] => {
    if (!dependentEntry || !boardKey) return [];
    const offered = new Set<string>(variablesForControls(nextControls));
    const stranded = [...dependentEntry.tokens].filter((id) => {
      const text = templateText(dashboardState.widgets[id]?.spec);
      return parseTokens(text ?? "").some(
        (token) => token.prefixed && token.key === boardKey && !offered.has(token.variable),
      );
    });
    return widgetLabels(stranded, dashboardState.widgets, slicerValues);
  };

  /**
   * The widgets that would lose an effective binding if this kind were removed. The
   * collapsed bindings depend on their host kind — a single-player binding on "subject"
   * and a single-date binding on "date" — unless the controller still carries the
   * matching legacy kind, which also satisfies them.
   */
  const bindingStranded = (kind: ControllerKind): string[] => {
    if (kind === "subject" && !state.controls.includes("single_player")) {
      return unique([...boundWidgetTitles.subject, ...boundWidgetTitles.single_player]);
    }
    if (kind === "date" && !state.controls.includes("single_date")) {
      return unique([...boundWidgetTitles.date, ...boundWidgetTitles.single_date]);
    }
    if (kind === "single_player" && state.controls.includes("subject")) {
      return [];
    }
    if (kind === "single_date" && state.controls.includes("date")) {
      return [];
    }
    return boundWidgetTitles[kind];
  };

  const strandedByRemoval = (kind: ControllerKind): string[] =>
    unique([
      ...bindingStranded(kind),
      ...tokenStranded(state.controls.filter((c) => c !== kind)),
    ]);

  const toggleControl = (kind: ControllerKind, checked: boolean) => {
    if (!checked) {
      const blocked = strandedByRemoval(kind);
      if (blocked.length > 0) {
        confirmControlsNarrowingBlocked(CONTROL_LABELS[kind], blocked);
        return;
      }
      const next = state.controls.filter((c) => c !== kind);
      if (next.length === 0) return;
      dispatch({ type: "SET_CONTROLS", controls: next });
      return;
    }
    if (state.controls.includes(kind)) return;
    dispatch({
      type: "SET_CONTROLS",
      controls: CONTROLLER_KINDS.filter((c) => c === kind || state.controls.includes(c)),
    });
  };

  /**
   * A controller holds exactly ONE picker, chosen by radio. A saved controller that still
   * carries several kinds (or a legacy kind) keeps the checkbox editor with its guards, so
   * the coach can narrow it down without stranding the widgets that follow it.
   */
  const isLegacyMulti =
    state.controls.length > 1 ||
    state.controls.some((c) => !SELECTABLE_CONTROLLER_KINDS.includes(c));

  const selectControl = (kind: ControllerKind) => {
    const current = state.controls[0];
    if (kind === current) return;
    const blocked = unique([...bindingStranded(current), ...tokenStranded([kind])]);
    if (blocked.length > 0) {
      confirmControlsNarrowingBlocked(CONTROL_LABELS[current], blocked);
      return;
    }
    dispatch({ type: "SET_CONTROLS", controls: [kind] });
  };

  // The legacy checkboxes stay visible only while the saved controller still carries
  // their kinds, so the coach can retire them — no new controller can gain one.
  const visibleKinds = CONTROLLER_KINDS.filter(
    (kind) => SELECTABLE_CONTROLLER_KINDS.includes(kind) || state.controls.includes(kind),
  );

  const form = (
    <>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8ecf0" }}>
        <Space size={8} align="center" style={{ width: "100%" }}>
          <Text style={{ fontSize: 12, minWidth: 40 }}>Title</Text>
          <Input
            size="small"
            placeholder="e.g. Board Controls"
            value={state.label}
            onChange={(e) => dispatch({
              type: "SET_LABEL",
              label: e.target.value,
              key: boardKey ? undefined : deriveKey(e.target.value, otherKeys),
            })}
            style={{ flex: 1 }}
          />
          <Text style={{ fontSize: 12 }}>Key</Text>
          <Text code aria-label="Slicer key" type={problem ? "danger" : undefined}>
            {state.key}
          </Text>
          <Text style={{ fontSize: 12 }}>Color</Text>
          <ColorPicker
            size="small"
            value={state.slice_color}
            disabledAlpha
            presets={[{ label: "Slicer colors", colors: [...SLICE_COLOR_PALETTE] }]}
            onChange={(c) => dispatch({ type: "SET_SLICE_COLOR", slice_color: c.toHexString() })}
          />
        </Space>
        <Text
          type={problem ? "danger" : "secondary"}
          style={{ fontSize: 11, display: "block", marginTop: 4 }}
        >
          {problem ?? (
            <>
              Titles and text quote this slicer as{" "}
              <code>{`{Slicer.${state.key}.${exampleVariable}}`}</code>.
              {boardKey ? "" : " The key follows the title until you add the slicer."}
            </>
          )}
        </Text>
      </div>

      <Collapse
        accordion
        activeKey={state.expandedKey}
        onChange={(key) => dispatch({ type: "SET_EXPANDED_KEY", key })}
        style={{ borderRadius: 0, borderTop: "none" }}
      >
        <Panel
          key="__controls"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">1</span>
              <Text strong style={{ fontSize: 13 }}>Pickers</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {state.controls.map((c) => CONTROL_SHORT_LABELS[c]).join(" · ")}
              </Text>
            </div>
          }
        >
          <div className="awm-step-body">
            <Text type="secondary" style={{ fontSize: 12 }}>
              Choose the one picker this slicer shows. Set its values on the slicer
              itself once it is on the dashboard. A body map that follows this slicer
              shows the last day of the date range. A body map or player card shows the
              first athlete of the Athletes &amp; drills picker.
            </Text>
            {isLegacyMulti ? (
              visibleKinds.map((kind) => (
                <Checkbox
                  key={kind}
                  checked={state.controls.includes(kind)}
                  onChange={(e) => toggleControl(kind, e.target.checked)}
                >
                  <Text style={{ fontSize: 12 }}>{CONTROL_LABELS[kind]}</Text>
                </Checkbox>
              ))
            ) : (
              <Radio.Group
                value={state.controls[0]}
                onChange={(e) => selectControl(e.target.value as ControllerKind)}
              >
                <Space direction="vertical" size={4}>
                  {SELECTABLE_CONTROLLER_KINDS.map((kind) => (
                    <Radio key={kind} value={kind}>
                      <Text style={{ fontSize: 12 }}>{CONTROL_LABELS[kind]}</Text>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            )}
          </div>
        </Panel>

      </Collapse>
    </>
  );

  const preview = (
    <PreviewShell title={state.label || "Slicer"} hasContent>
      <Alert
        type="info"
        showIcon
        message={
          <Text style={{ fontSize: 11 }}>
            Open a widget's Date Range or Filters section and check "Controlled by Slicer"
            to make it follow this slicer. Set the pickers' values on the slicer
            itself, on the dashboard.
          </Text>
        }
        style={{ marginBottom: 10 }}
      />
      <div className="dynamic-controller-preview">
        {state.controls.map((kind) => (
          <div key={kind} className="dynamic-controller-preview-row">
            <Text strong style={{ fontSize: 11 }}>{CONTROL_SHORT_LABELS[kind]}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{CONTROL_LABELS[kind]}</Text>
          </div>
        ))}
      </div>
    </PreviewShell>
  );

  return <ConfigPanelLayout form={form} preview={preview} />;
}
